import React, { useMemo } from "react";
import {
  FIT_OPTIONS,
  INFLUENCE_OPTIONS,
  WASH_OPTIONS,
  influenceDisplayLabel,
  isForecastInfluence,
  isLibraryInfluence,
  isMaterialLengthOnlyInfluence,
  isUploadInfluence,
  materialsOptionsFor,
  necklineOptionsFor,
  sleeveOptionsFor,
} from "../../constants/imageGeneratorDictionaries";
import {
  FieldBlock,
  FieldSelect,
  lookFieldsShellClassName,
  sectionClassName,
  sectionTitleClassName,
} from "./ImageGeneratorControls";

type FollowUp = "neckline" | "sleeve" | "material" | "fit" | "wash" | "length";

type Props = {
  sectionTitle?: string;
  market?: string;
  productType: string;
  productTypeOptions: string[];
  onProductTypeChange: (value: string) => void;
  influence: string;
  onInfluenceChange: (value: string) => void;
  styleType: string;
  styleTypeOptions: string[];
  onStyleTypeChange: (value: string) => void;
  material: string;
  onMaterialChange: (value: string) => void;
  sleeve: string;
  onSleeveChange: (value: string) => void;
  neckline: string;
  onNecklineChange: (value: string) => void;
  length: string;
  lengthOptions: string[];
  onLengthChange: (value: string) => void;
  fit: string;
  onFitChange: (value: string) => void;
  wash: string;
  onWashChange: (value: string) => void;
  /** Fields that apply for the current product / influence / path. */
  visibleFollowUps: readonly FollowUp[];
  /**
   * Specification image or its preview is set. Product Type, Influence, and
   * Material (when that follow-up applies) stay. Other style details hide
   * without clearing their values.
   */
  specificationImageActive?: boolean;
  /**
   * Cells for the row under the style filters: specification image, Choose
   * Trend, then selected sketches. Pass a fragment of grid cells (not a flex
   * row) so they share this grid's column tracks.
   */
  specificationSlot?: React.ReactNode;
};

const INFLUENCE_FIELD_OPTIONS = INFLUENCE_OPTIONS.map(item => ({
  value: item,
  label: influenceDisplayLabel(item),
}));

const hasOption = (options: readonly string[], value: string) =>
  options.includes(value);

const FOLLOW_UP_ORDER: FollowUp[] = [
  "neckline",
  "sleeve",
  "material",
  "length",
  "fit",
  "wash",
];

const FOLLOW_UP_LABELS: Record<FollowUp, string> = {
  material: "Material",
  sleeve: "Sleeve",
  neckline: "Neckline",
  length: "Length",
  fit: "Fit",
  wash: "Wash",
};

/** Fill the shared grid cell; column track itself is capped at Select width. */
const GRID_FIELD_CLASS = "min-w-0 w-full";

/** Equal columns aligned across rows, max 207px (Select input width), min 132px. */
const lgColsClass = (count: number): string => {
  switch (Math.min(7, Math.max(2, count))) {
    case 2:
      return "lg:grid-cols-[repeat(2,minmax(132px,207px))]";
    case 3:
      return "lg:grid-cols-[repeat(3,minmax(132px,207px))]";
    case 4:
      return "lg:grid-cols-[repeat(4,minmax(132px,207px))]";
    case 5:
      return "lg:grid-cols-[repeat(5,minmax(132px,207px))]";
    case 6:
      return "lg:grid-cols-[repeat(6,minmax(132px,207px))]";
    default:
      return "lg:grid-cols-[repeat(7,minmax(132px,207px))]";
  }
};

const ImageGeneratorStyleFields: React.FC<Props> = ({
  sectionTitle = "Select Style",
  market,
  productType,
  productTypeOptions,
  onProductTypeChange,
  influence,
  onInfluenceChange,
  styleType,
  styleTypeOptions,
  onStyleTypeChange,
  material,
  onMaterialChange,
  sleeve,
  onSleeveChange,
  neckline,
  onNecklineChange,
  length,
  lengthOptions,
  onLengthChange,
  fit,
  onFitChange,
  wash,
  onWashChange,
  visibleFollowUps,
  specificationImageActive = false,
  specificationSlot = null,
}) => {
  const productSelected = productTypeOptions.includes(productType);
  const influenceSelected = INFLUENCE_FIELD_OPTIONS.some(
    option => option.value === influence
  );
  const styleSelected = styleTypeOptions.includes(styleType);
  const styleReady = productSelected && styleTypeOptions.length > 0;
  // Progressive disclosure: Style Type + follow-ups only after Influence.
  // Forecast / upload supply the style. A specification image hides Style Type too.
  const showStyleType =
    influenceSelected &&
    !specificationImageActive &&
    !isForecastInfluence(influence) &&
    !isUploadInfluence(influence);
  /** Library path: Style Type + product-listed follow-ups show as required. */
  const libraryPath = isLibraryInfluence(influence);
  /**
   * Forecast / Upload: Material + Length show as required in UI and hard-block
   * advancing (same path as Product Type / Influence in ImageGenerator).
   */
  const materialLengthOnlyPath = isMaterialLengthOnlyInfluence(influence);
  const followUpsRequired = libraryPath || materialLengthOnlyPath;

  const necklineOptions = useMemo(() => necklineOptionsFor(market), [market]);
  const sleeveOptions = useMemo(() => sleeveOptionsFor(market), [market]);
  const materialOptions = useMemo(
    () => materialsOptionsFor(productType),
    [productType]
  );

  // Influence gates the row; field set still comes from visibleFollowUps (Task 3).
  // Library + specification image keeps Material only. Upload influence keeps
  // Material + Length. Hidden values stay in parent state.
  const applicableFollowUps = influenceSelected
    ? FOLLOW_UP_ORDER.filter(field => visibleFollowUps.includes(field))
    : [];
  const displayedFollowUps =
    specificationImageActive && !isUploadInfluence(influence)
      ? applicableFollowUps.filter(field => field === "material")
      : applicableFollowUps;

  const followUpColumnCount =
    (showStyleType ? 1 : 0) + displayedFollowUps.length;
  const showFollowUpRow =
    influenceSelected &&
    (showStyleType ||
      displayedFollowUps.length > 0 ||
      Boolean(specificationSlot));
  /*
    Keep Product Type / Influence on the same lg column tracks as Style Type /
    Material / Length so box widths match across the two stacked grids.
  */
  const lookColumnCount = showFollowUpRow
    ? Math.max(2, followUpColumnCount)
    : 2;
  const lookLgColsClass = lgColsClass(lookColumnCount);
  /*
    Follow-up grid only (row 2+). Odd filter counts leave the small-screen
    2-column grid short a cell, and a single follow-up uses a 2-column lg
    template. One pad (shown only at the breakpoint that needs it) starts
    the specification / Choose Trend cell on column 1.
  */
  const specRowPadClassName =
    followUpColumnCount % 2 === 1
      ? followUpColumnCount >= 2
        ? "hidden min-w-0 sm:block lg:hidden"
        : "hidden min-w-0 sm:block"
      : "";

  const followUpControl = (field: FollowUp, enabled: boolean) => {
    switch (field) {
      case "material":
        return (
          <FieldSelect
            value={
              enabled && hasOption(materialOptions, material) ? material : ""
            }
            placeholder="Select material"
            onChange={onMaterialChange}
            disabled={!enabled}
            searchable
            options={materialOptions.map(item => ({
              value: item,
              label: item,
            }))}
          />
        );
      case "sleeve":
        return (
          <FieldSelect
            value={
              enabled && sleeveOptions.some(option => option.value === sleeve)
                ? sleeve
                : ""
            }
            placeholder="Select sleeve"
            onChange={onSleeveChange}
            disabled={!enabled}
            options={sleeveOptions}
          />
        );
      case "neckline":
        return (
          <FieldSelect
            value={
              enabled &&
              necklineOptions.some(option => option.value === neckline)
                ? neckline
                : ""
            }
            placeholder="Select neckline"
            onChange={onNecklineChange}
            disabled={!enabled}
            options={necklineOptions}
          />
        );
      case "length":
        return (
          <FieldSelect
            value={enabled && lengthOptions.includes(length) ? length : ""}
            placeholder="Select length"
            onChange={onLengthChange}
            disabled={!enabled || lengthOptions.length === 0}
            options={lengthOptions.map(item => ({
              value: item,
              label: item,
            }))}
          />
        );
      case "fit":
        return (
          <FieldSelect
            value={enabled && hasOption(FIT_OPTIONS, fit) ? fit : ""}
            placeholder="Select fit"
            onChange={onFitChange}
            disabled={!enabled}
            options={FIT_OPTIONS.map(item => ({ value: item, label: item }))}
          />
        );
      case "wash":
        return (
          <FieldSelect
            value={enabled && hasOption(WASH_OPTIONS, wash) ? wash : ""}
            placeholder="Select wash"
            onChange={onWashChange}
            disabled={!enabled}
            options={WASH_OPTIONS.map(item => ({ value: item, label: item }))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={sectionClassName}>
      <div className={lookFieldsShellClassName}>
        <h2 className={`${sectionTitleClassName} mb-3`}>{sectionTitle}</h2>

        {/*
          Progressive / row-wise disclosure:
          Row 1 = Product Type | Influence only (always its own grid).
          After Influence: a separate lower grid for Style Type / follow-ups /
          upload / Choose Trend — never shares row 1 cells.
          Both grids share lookLgColsClass so column widths match across rows.
          Column tracks cap at Select input width (207px) and can shrink.
        */}
        <div className="flex w-full flex-col gap-y-4">
          <div
            className={`grid w-full grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 ${lookLgColsClass}`}
          >
            <FieldBlock
              label="Product Type"
              required
              className={GRID_FIELD_CLASS}
            >
              <FieldSelect
                value={productSelected ? productType : ""}
                placeholder="Select product type"
                onChange={onProductTypeChange}
                options={productTypeOptions.map(item => ({
                  value: item,
                  label: item,
                }))}
              />
            </FieldBlock>
            <FieldBlock label="Influence" required className={GRID_FIELD_CLASS}>
              <FieldSelect
                value={influenceSelected ? influence : ""}
                placeholder="Select influence.."
                onChange={onInfluenceChange}
                options={INFLUENCE_FIELD_OPTIONS}
              />
            </FieldBlock>
          </div>

          {showFollowUpRow ? (
            <div
              className={`grid w-full grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 ${lookLgColsClass}`}
            >
              {showStyleType ? (
                <FieldBlock
                  label="Style Type"
                  required={libraryPath}
                  className={GRID_FIELD_CLASS}
                >
                  <FieldSelect
                    value={styleSelected ? styleType : ""}
                    placeholder="Select style type"
                    onChange={onStyleTypeChange}
                    disabled={!styleReady}
                    options={styleTypeOptions.map(item => ({
                      value: item,
                      label: item,
                    }))}
                  />
                </FieldBlock>
              ) : null}

              {displayedFollowUps.map(field => {
                const enabled = productSelected;
                return (
                  <FieldBlock
                    key={field}
                    label={FOLLOW_UP_LABELS[field]}
                    required={enabled && followUpsRequired}
                    className={GRID_FIELD_CLASS}
                  >
                    {followUpControl(field, enabled)}
                  </FieldBlock>
                );
              })}
              {specificationSlot ? (
                <>
                  {specRowPadClassName ? (
                    <div className={specRowPadClassName} aria-hidden="true" />
                  ) : null}
                  {specificationSlot}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ImageGeneratorStyleFields;
